import { appColors } from '@base/useTheme';
import {
    ColorBy,
    ColorSwatch,
    ChartColumnVisualization,
    ChartLabelAlign,
    ChartLabelOptions,
    ChartMarkerStyle,
    ChartRowVisualization,
    ChartTileVisualization,
    ChartSeriesType,
    ChartType,
    EntityType,
    NumberFormatComponent,
    NumberFormatPolarity,
    SheetCell,
    SheetColumn,
    SheetRowRepresentation,
    TileDataSheetBackedWithSheet,
} from '@common/api';
import {
    ChartData,
    ChartDatumPoint,
    ChartSeries,
    ChartView,
    DEFAULT_CHART,
    EChart,
    EChartPie,
    seriesToWaterfall,
    GROUPED_CHARTS,
    STACKED_CHARTS,
    toChartSeriesType,
    groupRowsByDepth,
    getCanonicalColumns,
    getCanonicalRows,
    CHART_SHEET_OPTIONS,
    CHART_SHEET_OPTIONS_GROUPED,
} from '@components/chart';
import { DividerVertical } from '@components/layout/Divider';
import { FlexRow, IsolateAbsolute } from '@components/layout/Flex';
import { useSheetColumnConfig } from '@components/sheet/Sheet';
import { useAllEntities } from '@data/entity/entity';
import { DEFAULT_SWATCH } from '@features/colors/ColorPicker';
import { SheetEditorSort } from '@features/sheet/editor/SheetEditorSort';
import { SheetEditorFilters } from '@features/sheet/editor/SheetEditorFilters';
import { SheetEditorContext } from '@features/sheet/editor/SheetEditorProvider';
import { SheetEditorTimeline } from '@features/sheet/editor/SheetEditorTimeline';
import { useSheetState } from '@features/sheet/editor/sheetState';
import { useAuth } from '@providers/AuthProvider';
import { useColorContext } from '@providers/ColorProvider';
import { useEntityContext } from '@providers/EntityProvider';
import { FormattingFunctions, useFormatting } from '@providers/FormattingProvider';
import { patch } from '@use-gpu/state';
import { seq } from '@util/array';
import { ColorPalette } from '@util/enums';
import { DeepRequired } from '@util/patch';
import { memo, useCallback, useMemo } from 'react';
import { DashboardTileContent, DashboardTileFilters } from '../Dashboard.style';
import { useTileConfigSync } from './useTileConfig';
import { useTileGlobalOverride } from './useTileGlobalOverride';

export const LabelSmartFilterType = {
    NONE: 'None',
    CHANGE: 'Change',
};

export type RowOrColumnLike = {
    key: string;
    name: string;
    cells?: SheetCell[];
};

export const ChartTile = memo(
    ({
        data,
        background,
        canEdit,
        canDataEntry,
        hasTitle,
    }: {
        data: TileDataSheetBackedWithSheet;
        background?: string;
        canEdit: boolean;
        canDataEntry: boolean;
        hasTitle?: boolean;
    }) => {
        const {
            settings: { user_color_palette },
        } = useAuth();

        const chartVisualization = useMemo(
            () => patch(DEFAULT_CHART, data.visualization.chart) as DeepRequired<ChartTileVisualization>,
            [data]
        );
        const colorBlind = user_color_palette === ColorPalette.COLORBLIND;
        const grouped = GROUPED_CHARTS.includes(chartVisualization.type) && chartVisualization.series.groupByDepth;

        const globalOverride = useTileGlobalOverride(data);
        const localOverride = useMemo(
            () => ({
                ...globalOverride,
                ...CHART_SHEET_OPTIONS,
                ...(grouped ? CHART_SHEET_OPTIONS_GROUPED : undefined),
            }),
            [globalOverride, grouped]
        );
        const formatting = useFormatting();

        const sheetPath = `${data.sheet.id}`;
        const sheetContext = useSheetState(sheetPath, canDataEntry, canEdit, localOverride);
        const {
            state: {
                sheetConfig,
                sheetConfig: {
                    columns: {
                        intervalToolbar: { enabled: intervalToolbarEnabled },
                    },
                },
                sheetData: { rows, columns },
                canSaveForEveryone,
            },
            api: { resetConfig, saveForEveryone },
        } = sheetContext;
        const { orderedColumns, columnLookup } = useSheetColumnConfig(columns, sheetConfig.display.columnConfig);

        useTileConfigSync(canSaveForEveryone, saveForEveryone, resetConfig);

        const { defaultPalette } = useColorContext();
        const chartBackground = background ?? '#FFFFFF';

        const version = useAllEntities({ type: EntityType.Variable });
        const { getEntity } = useEntityContext();
        const resolveVariable = useCallback(
            (variableId?: number) => {
                if (variableId == null) return '';
                return getEntity(EntityType.Variable, variableId)?.meta.name ?? '';
            },
            [getEntity]
        );

        const chartData = useMemo(
            () =>
                getChartData(
                    rows,
                    orderedColumns,
                    columnLookup,
                    defaultPalette,
                    chartBackground,
                    colorBlind,
                    chartVisualization,
                    formatting,
                    resolveVariable
                ),
            [
                rows,
                orderedColumns,
                columnLookup,
                defaultPalette,
                chartBackground,
                colorBlind,
                chartVisualization,
                formatting,
                resolveVariable,
                // eslint-disable-next-line react-hooks/exhaustive-deps
                version,
            ]
        );

        const isPie = chartVisualization.type === ChartType.PieChart;
        const Component = isPie ? EChartPie : EChart;

        const showSortBar = !data.useDashboardSort && data.showTileSort;
        const showFilterBar = !data.useDashboardFilters && data.showTileFilters;

        const showTimeline = !data.useDashboardTimeline && data.showTileTimeline && intervalToolbarEnabled;
        const reserveToolbarSpace = !hasTitle && showFilterBar ? 100 : 0;

        // Reset the chart when the toolbars are added/removed to avoid layout jitter
        const contentKey = +showFilterBar * 2 + +showTimeline;

        return (
            <SheetEditorContext.Provider value={sheetContext}>
                <DashboardTileContent>
                    {(showSortBar || showFilterBar) && (
                        <DashboardTileFilters $reserve={reserveToolbarSpace}>
                            <IsolateAbsolute>
                                <FlexRow>
                                    {showSortBar ? <SheetEditorSort /> : null}
                                    {showSortBar && showFilterBar ? <DividerVertical /> : null}
                                    {showFilterBar ? <SheetEditorFilters /> : null}
                                </FlexRow>
                            </IsolateAbsolute>
                        </DashboardTileFilters>
                    )}
                    <DashboardTileContent $isolate>
                        <Component key={contentKey} {...chartData} colorBlind={colorBlind} />
                    </DashboardTileContent>
                    {showTimeline ? <SheetEditorTimeline /> : null}
                </DashboardTileContent>
            </SheetEditorContext.Provider>
        );
    }
);

ChartTile.displayName = 'ChartTile';

const getRowName = (row: RowOrColumnLike) => row.cells?.[0]?.value?.string ?? row.name;

const getChartData = (
    rows: SheetRowRepresentation[],
    orderedColumns: SheetColumn[],
    columnLookup: number[],
    colorPalette: string[],
    background: string,
    colorBlind: boolean,
    visualization: DeepRequired<ChartTileVisualization>,
    formatting: FormattingFunctions,
    resolveVariable: (variableId?: number) => string
): ChartData => {
    const {
        type,
        layout,
        spacing,
        display,
        labels: { labelFormat },
        series: { transposeData, normalizeData, keepEmptyDomain, keepEmptyValue, groupByDepth },
        rowVisualizationByPath,
        columnVisualizationByPath,
    } = visualization;

    // Keep only number columns
    const { keepIndices: orderedIndices, numberColumns } = getCanonicalColumns(orderedColumns);
    const numberColumnsLookup: number[] = orderedIndices.map((i) => columnLookup[i]);

    const data: ChartSeries[] = [];
    const stacked = STACKED_CHARTS.includes(type);
    const normalized = stacked && !!normalizeData;
    const grouped = GROUPED_CHARTS.includes(type) && !!groupByDepth;

    // Prepare formatter for generated stats
    const { formatNumber } = formatting;
    const formatter =
        labelFormat?.formatString != null ? (x: number) => formatNumber(x, labelFormat.formatString) : undefined;

    // Prepare to group rows by hierarchy into sub-views
    let views: ChartView[] | undefined;
    let categories: string[] = [];
    let categoryIndexMap: Map<string, number> | undefined;
    let categoryRowMap: Map<string, SheetRowRepresentation> | undefined;
    let groupedRows: SheetRowRepresentation[][] = [rows];

    if (rows && rows.length > 0 && numberColumnsLookup.length > 0) {
        let groupedCategories;
        if (grouped) {
            // If split, each group of rows may be unique.
            // Unify categories across all groups first.
            ({ groupedRows, views } = groupRowsByDepth(rows));
            ({
                indexMap: categoryIndexMap,
                rowMap: categoryRowMap,
                categories: groupedCategories,
            } = getCanonicalRows(groupedRows));
        }

        if (transposeData) {
            categories = groupedCategories ?? rows.map((row) => getRowName(row));
        } else {
            categories = numberColumns.map((column) => column.name);
        }

        for (const [viewIndex, rows] of groupedRows.entries()) {
            if (transposeData) {
                // rows as columns
                const columnVisualizations = rows.map((row) => columnVisualizationByPath[row.key] ?? null);

                numberColumns.forEach((column, idx) => {
                    // columns as rows
                    const rowVisualization = rowVisualizationByPath[column.key];
                    const i = numberColumnsLookup[idx];
                    const color = rowVisualization?.color ?? { solid: colorPalette[idx % colorPalette.length] };

                    const cells = rows.map((row) => row.cells[i]);
                    const categories = categoryIndexMap
                        ? rows.map((row) => categoryIndexMap!.get(row.name)!)
                        : undefined;

                    const item = getChartSeries(
                        column,
                        cells,
                        categories,
                        viewIndex,
                        color,
                        visualization,
                        rowVisualization,
                        columnVisualizations,
                        formatting,
                        resolveVariable
                    );

                    if (item.data.length && (keepEmptyDomain || item.data.some((x) => !!x))) {
                        data.push(item);
                    }
                });
            } else {
                // columns as columns
                const columnVisualizations = numberColumns.map(
                    (column) => columnVisualizationByPath[column.key] ?? null
                );

                rows.forEach((row, idx) => {
                    // rows as rows
                    const canonicalKey = categoryRowMap?.get(getRowName(row))?.key ?? row.key;

                    const rowVisualization = rowVisualizationByPath[canonicalKey];
                    const color = rowVisualization?.color ?? { solid: colorPalette[idx % colorPalette.length] };

                    const cells = numberColumnsLookup.map((i) => row.cells[i]);

                    const item = getChartSeries(
                        row,
                        cells,
                        undefined,
                        viewIndex,
                        color,
                        visualization,
                        rowVisualization,
                        columnVisualizations,
                        formatting,
                        resolveVariable
                    );

                    if (item.data.length && (keepEmptyDomain || item.data.some((x) => !!x))) {
                        data.push(item);
                    }
                });
            }
        }
    }

    // Filter out categories that have 0 data points associated with them
    if (!keepEmptyValue) {
        const nonEmptyIndices = seq(categories.length).map((i) => data.some((series) => series.data[i] != null));
        for (const series of data) series.data = series.data.filter((_, i) => nonEmptyIndices[i]);
        categories = categories.filter((_, i) => nonEmptyIndices[i]);
    }

    return {
        data,
        categories,
        views,
        stacked,
        normalized,
        layout,
        spacing,
        display,
        background,
        colorBlind,
        formatter,
    };
};

const getChartSeries = (
    row: RowOrColumnLike,
    cells: SheetCell[],
    categories: number[] | undefined,
    viewIndex: number,
    color: ColorSwatch,
    visualization: DeepRequired<ChartTileVisualization>,
    rowVisualization: ChartRowVisualization | undefined,
    columnVisualizations: (ChartColumnVisualization | null)[],
    formatting: FormattingFunctions,
    resolveVariable: (variableId?: number) => string
): ChartSeries => {
    // Label options
    const labelOptions = patch(visualization.labels, rowVisualization?.labels) as DeepRequired<ChartLabelOptions>;
    const hasLabels = visualization.labels.showLabels !== false && labelOptions.showLabels !== false;
    const filterLabelsByChange = hasLabels && labelOptions.labelSmartFilter === LabelSmartFilterType.CHANGE;

    const totalColumns = columnVisualizations.map((cv, i) => i === 0 || !!cv?.total);

    const { key, name } = row;
    const label = row.cells?.[0]?.value?.string ?? name;

    const style = {
        color: patch(DEFAULT_SWATCH, color) as Required<ColorSwatch>,
        showLabel: hasLabels,

        labelBold: labelOptions.labelBold ?? false,
        labelItalic: labelOptions.labelItalic ?? false,
        labelOutline: labelOptions.labelOutline ?? true,
        labelUnderline: labelOptions.labelUnderline ?? false,
        labelColor: labelOptions.labelColor ?? appColors.ui.dark,
        labelFontSize: labelOptions.labelFontSize ? `${labelOptions.labelFontSize}px` : '12px',
        labelAngle: labelOptions.labelAngle ?? 0,
        labelAlign: labelOptions.labelAlign ?? ChartLabelAlign.Auto,
        labelOffset: [labelOptions.labelDX, labelOptions.labelDY] as [number, number],
    };

    // Series type by chart type or by series (combo chart)
    const seriesType = toChartSeriesType(visualization.type) ?? rowVisualization?.type ?? ChartSeriesType.BarChart;
    const seriesMarker = rowVisualization?.marker ?? ChartMarkerStyle.Dot;

    // Gather colored/labeled data points
    const points = getSeriesPoints(
        cells,
        seriesType,
        color,
        categories,
        totalColumns,
        labelOptions.labelFormat,
        rowVisualization?.accumulateData,
        formatting,
        resolveVariable
    );

    // Only keep labels around local minima/maxima/edges
    if (filterLabelsByChange) {
        const n = points.length;
        for (let i = 1; i < n - 1; ++i) {
            const prev = points[i - 1]?.y;
            const curr = points[i]?.y;
            const next = points[i + 1]?.y;

            if (prev != null && curr != null && next != null) {
                const min = Math.min(prev, curr, next);
                const max = Math.max(prev, curr, next);

                if (curr !== min && curr !== max) {
                    points[i]!.label = '';
                }
            }
        }
    }

    return {
        key: `${key}-${viewIndex}`,
        label,
        viewIndex,
        type: seriesType,
        marker: seriesMarker,
        data: points,
        style,
    };
};

export const getSeriesPoints = (
    cells: SheetCell[],
    seriesType: ChartSeriesType,
    color: ColorSwatch,
    categories: number[] | undefined,
    totalColumns: boolean[] | undefined,
    labelFormat: Partial<NumberFormatComponent> | null | (Partial<NumberFormatComponent> | null)[] | undefined,
    accumulateData: boolean | undefined,
    formatting: FormattingFunctions,
    resolveVariable?: (variableId?: number) => string
): (ChartDatumPoint | null)[] => {
    const { formatCell } = formatting;
    const isWaterfall = seriesType === ChartSeriesType.WaterfallChart;

    const shouldAccumulateData = isWaterfall && (accumulateData ?? true);
    const shouldTrackPolarity = shouldAccumulateData;
    const colorByTrend = color.colorBy === ColorBy.Trend;
    const colorByValue = color.colorBy === ColorBy.Value;

    // Track polarity as it may change from row to row
    let valueSign = 1.0; // whether values are sign-flipped
    let trendSign = 1.0; // whether trend colors are sign-flipped
    let trendPolarity: NumberFormatPolarity | null = null;

    // Format series data
    let last = 0;
    let accumulator = 0;
    let points = cells.map((cell, i) => {
        if (cell?.value?.number != null) {
            // Resolve row format
            const resolvedFormat = Array.isArray(labelFormat) ? labelFormat[i] : labelFormat;

            // Check value + polarity
            const value = cell.value.number || 0;
            const cellPolarity = resolvedFormat?.polarity ?? cell.value.polarity ?? NumberFormatPolarity.Positive;

            if (!trendPolarity) {
                trendPolarity = cellPolarity;
                trendSign = cellPolarity === NumberFormatPolarity.Negative ? -1 : 1;
                // valueSign always starts as +1
            }
            // If row polarity switches, flip trend colors (and optionally value sign)
            else if (cellPolarity !== trendPolarity) {
                trendPolarity = cellPolarity;
                trendSign = -trendSign;
                if (shouldTrackPolarity) {
                    valueSign = -valueSign;
                }
            }

            // Get formatted label
            const formattedCell =
                resolvedFormat && cell.value
                    ? {
                          ...cell.value,
                          format: resolvedFormat.formatString ?? cell.value.format,
                          ...resolvedFormat,
                      }
                    : cell.value;
            const formattedValue = formatCell(formattedCell);

            // Accumulate totals if requested
            const v = value * valueSign;
            let y = accumulator + v;
            if (shouldAccumulateData) {
                if (totalColumns?.[i]) y = accumulator = v;
                else accumulator += v;
            }

            // Combine trend and value flip (they cancel out)
            const flipSign = trendSign * valueSign;

            const positive = color.positive ?? appColors.success.w500;
            const negative = color.negative ?? appColors.error.w500;
            const neutral = (colorByValue || colorByTrend ? color.neutral : color.solid) ?? appColors.ui.dark;

            // Get trend value + trend color
            const colorAsRelative = color.colorTotals || !totalColumns?.[i];
            const thresholdValue = colorByValue ? color.threshold || 0 : 0;
            const trendValue = (colorByTrend && !totalColumns?.[i] ? y - last : y) * flipSign;
            const trendColor =
                colorByValue || (colorByTrend && colorAsRelative)
                    ? trendValue > thresholdValue
                        ? positive
                        : trendValue < thresholdValue
                        ? negative
                        : neutral
                    : neutral;
            last = y;

            return {
                x: categories?.[i] ?? i,
                y,
                y0: 0,
                polarity: Math.sign(trendSign),
                sign: Math.sign(v),
                color: trendColor ?? '#000',
                label: formattedValue,
                origin: resolveVariable?.(cell.variableId),
                formattedValue,
            };
        }
        if (cell?.value?.string != null || cell?.value?.stringList != null) {
            const formattedValue = cell?.value?.string ?? cell?.value?.stringList?.join(', ') ?? '';
            return {
                x: categories?.[i] ?? i,
                y: 0,
                y0: 0,
                polarity: NaN,
                sign: NaN,
                color: '#000',
                label: formattedValue,
                formattedValue,
            };
        }
        return null;
    });

    if (isWaterfall) {
        points = seriesToWaterfall(points, totalColumns);
    }
    return points;
};
